/**
 * 先画到半精度浮点缓冲，再做一次保色相的色调映射。
 *
 * 一天几百张照片时，星团在远处挤在几个像素里，加法混合会叠出远大于 1 的值。
 * 直接输出会冲成一片白；这里按最亮的通道整体压缩，越密越亮，但颜色还是星自己的颜色。
 *
 * 色调映射之前先加辉光（three.js 自带的 UnrealBloomPass，参数同原型），亮的星点、光晕、爱心会晕开。
 * 叠加层（照片面片、轮转光环里的卡片、选中星的圈）画在单独的缓冲上，色调映射之后才叠上来：
 * 不会像原型实测的那样被辉光晕成白块，也不会被色调映射压暗，但和星光一样跟着流体扭曲、色散（同原型 §4.4）。
 *
 * 最后合成也在这一步，和原型一样：流体速度场扭曲画面、沿流向的色散、染色光晕、暗角、颗粒，
 * 再加上入场用的白光（uFlash）和画面撕裂（uGlitch）。爆炸冲击波就是流体里的两圈推力（fluid.ts）。
 */
import * as THREE from 'three';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';

const VERT = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = position.xy * 0.5 + 0.5;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform sampler2D tScene;
uniform sampler2D tVel;
uniform sampler2D tDye;
uniform sampler2D tCards;
uniform float uCards;
uniform float uExposure;
uniform float uTime;
uniform float uFlash;
uniform float uGlitch;
uniform float uDistort;
uniform float uAberr;
uniform float uDye;
uniform float uVignette;
uniform float uGrain;
uniform vec2 uRes;
varying vec2 vUv;

float hash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }

void main() {
  // 流体把画面推开，推得越快色散越明显
  vec2 off = texture2D(tVel, vUv).xy * uDistort;
  float m = length(off);
  if (m > 0.05) {
    off *= 0.05 / m;
    m = 0.05;
  }
  vec2 dir = m > 1e-5 ? off / m : vec2(0.0);
  vec2 ab = dir * min(m * uAberr, 0.01);
  vec2 uv = vUv - off;
  if (uGlitch > 0.0) {
    // 画面崩溃：横向错位的条带 + RGB 分离
    float tq = floor(uTime * 24.0), r1 = hash(vec2(floor(vUv.y * 42.0), tq));
    uv.x += (r1 - 0.5) * 0.09 * uGlitch * step(0.62, r1);
    ab += vec2(0.014 * uGlitch, 0.0);
  }
  vec3 c = dot(ab, ab) > 0.0
    ? vec3(texture2D(tScene, uv - ab).r, texture2D(tScene, uv).g, texture2D(tScene, uv + ab).b)
    : texture2D(tScene, uv).rgb;
  c *= uExposure;
  // 加法混合万一溢出成无穷大或 NaN，按黑处理
  if (any(isnan(c)) || any(isinf(c))) c = vec3(0.0);
  c = max(c, vec3(0.0));
  float mx = max(max(c.r, c.g), c.b);
  // 0.6 以下原样，以上平滑地压到 1，斜率连续
  float k = 0.6;
  float mm = mx < k ? mx : k + (1.0 - k) * (1.0 - exp(-(mx - k) / (1.0 - k)));
  vec3 o = mx > 1e-5 ? c * (mm / mx) : c;
  // 特别密的地方稍微泛白，像星团的亮核
  o = mix(o, vec3(1.0), smoothstep(6.0, 40.0, mx) * 0.35);
  if (uCards > 0.0) {
    // 照片卡片（预乘透明度）叠在星光上面，用同一个扭曲过的坐标
    vec4 card = texture2D(tCards, uv);
    if (dot(ab, ab) > 0.0) {
      card.r = texture2D(tCards, uv - ab).r;
      card.b = texture2D(tCards, uv + ab).b;
    }
    o = o * (1.0 - card.a) + card.rgb;
  }
  o += texture2D(tDye, vUv).rgb * uDye;
  vec2 q = vUv - 0.5;
  q.x *= uRes.x / uRes.y;
  o *= 1.0 - uVignette * smoothstep(0.45, 1.25, length(q));
  o *= 1.0 + (hash(vUv * uRes + fract(uTime) * 100.0) - 0.5) * uGrain;
  o = clamp(o + uFlash * vec3(1.0, 0.92, 0.95), 0.0, 1.0);
  gl_FragColor = vec4(o, 1.0);
  #include <colorspace_fragment>
}
`;

export class ToneMapPass {
  readonly target = new THREE.WebGLRenderTarget(1, 1, { type: THREE.HalfFloatType, depthBuffer: true });
  /** 辉光：亮度过阈值的部分模糊后加回半精度缓冲，再一起做色调映射 */
  readonly bloom = new UnrealBloomPass(new THREE.Vector2(1, 1), 0.9, 0.55, 0.2);
  /** 上一帧画场景本身用了几次绘制（不含辉光和最后合成） */
  sceneCalls = 0;
  /** 照片卡片的缓冲：第一次轮转时才建；sRGB 编码的 8 位，暗部不出色带 */
  private cardTarget: THREE.WebGLRenderTarget | null = null;
  private readonly size = new THREE.Vector2(1, 1);
  private readonly clearColor = new THREE.Color();
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  readonly uniforms = {
    tScene: { value: this.target.texture },
    tVel: { value: null as THREE.Texture | null },
    tDye: { value: null as THREE.Texture | null },
    tCards: { value: null as THREE.Texture | null },
    uCards: { value: 0 },
    uExposure: { value: 1 },
    uTime: { value: 0 },
    uFlash: { value: 0 },
    uGlitch: { value: 0 },
    // 原型的取值：速度 1000 时画面偏移约 0.2，封顶 0.05
    uDistort: { value: 0.0002 },
    uAberr: { value: 0.5 },
    uDye: { value: 0.32 },
    uVignette: { value: 0.55 },
    uGrain: { value: 0.07 },
    uRes: { value: new THREE.Vector2(1, 1) },
  };

  constructor() {
    const g = new THREE.BufferGeometry();
    // 一个盖住全屏的大三角形
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    const quad = new THREE.Mesh(g, new THREE.ShaderMaterial({ vertexShader: VERT, fragmentShader: FRAG, uniforms: this.uniforms, depthTest: false, depthWrite: false }));
    quad.frustumCulled = false;
    this.scene.add(quad);
  }

  /** 传绘制缓冲的像素尺寸 */
  setSize(width: number, height: number): void {
    this.size.set(width, height);
    this.target.setSize(width, height);
    this.cardTarget?.setSize(width, height);
    // 辉光内部从一半分辨率开始逐级缩小，和原型一样按绘制缓冲的像素尺寸算
    this.bloom.setSize(width, height);
    this.uniforms.uRes.value.set(width, Math.max(1, height));
  }

  /** cards：叠加层的场景（照片、轮转卡片），没有要画的就传 null */
  render(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera, cards: THREE.Scene | null = null): void {
    renderer.setRenderTarget(this.target);
    renderer.render(scene, camera);
    this.sceneCalls = renderer.info.render.calls;
    if (this.bloom.strength > 0) this.bloom.render(renderer, this.target, this.target, 0, false);
    if (cards) {
      if (!this.cardTarget) {
        this.cardTarget = new THREE.WebGLRenderTarget(this.size.x, this.size.y, { depthBuffer: false });
        this.cardTarget.texture.colorSpace = THREE.SRGBColorSpace;
      }
      const color = renderer.getClearColor(this.clearColor), alpha = renderer.getClearAlpha();
      renderer.setClearColor(0x000000, 0);
      renderer.setRenderTarget(this.cardTarget);
      renderer.render(cards, camera);
      renderer.setClearColor(color, alpha);
      this.uniforms.tCards.value = this.cardTarget.texture;
      this.uniforms.uCards.value = 1;
    } else this.uniforms.uCards.value = 0;
    renderer.setRenderTarget(null);
    renderer.render(this.scene, this.camera);
  }
}
